// pylee/apps/web/app/(home)/components/features.tsx
'use client';

import { useState } from 'react';
// --- Import icons from lucide-react ---
import { Library, ShieldCheck, BrainCircuit, type LucideProps } from 'lucide-react'; // Choose appropriate icons
// --- Import styles ---
import styles from './features.module.css';

// Define the structure for each feature's data
interface FeatureData {
  id: string; // Unique ID for state management
  tabLabel: string;
  icon: React.ComponentType<LucideProps>;
  title: string;
  points: string[];
  visualPlaceholderTitle: string; // Placeholder text for the card
  visualPlaceholderDescription: string; // Placeholder text for the card
}

// Define the feature data array
const featuresData: FeatureData[] = [
  {
    id: 'registry',
    tabLabel: 'Deployment & Hosting',
    icon: Library, // Icon for registry
    title: 'Host Private Registries & Deploy MCP Servers',
    points: [
      'One click deployment with automatic secrets management',
      'Host private registries based on use case or team',
      'Version control your configurations and edit tool descriptions',
      'Discover & share approved tools & resources in one secure place',
      'Accessing Anthropic\'s official registry and community servers',
    ],
    visualPlaceholderTitle: 'Centralized Tool Registry',
    visualPlaceholderDescription:
      'Pylee keeps all your AI tools organized and discoverable in one secure location.',
  },
  {
    id: 'gateway',
    tabLabel: 'Secrets & Security',
    icon: ShieldCheck, // Icon for security
    title: 'Enterprise-Grade Security with Granular Controls',
    points: [
      'Secure handling of API keys, tokens, and environment variables',
      'Secrets encrypted at rest and in transit',
      'Granular role-based access control (RBAC)',
      'IT review workflows before MCP server deployment',
      'Full audit trails of tool usage and access'
    ],
    visualPlaceholderTitle: 'Secure Gateway',
    visualPlaceholderDescription:
      'Pylee protects your AI operations with enterprise-level security and governance.',
  },
  {
    id: 'development',
    tabLabel: 'Internal MCP Servers',
    icon: BrainCircuit, // Icon for knowledge/development
    title: 'Build MCP Servers Without Barriers',
    points: [
      'Deploy your custom MCP servers with one click',
      'Test locally, deploy globally with proper access controls',
      'Wrap internal APIs as secure MCP servers',
      'Preserve institutional knowledge as reusable AI agents',
      'Share with your team or the community'
    ],
    visualPlaceholderTitle: 'Knowledge Capture',
    visualPlaceholderDescription:
      "Pylee transforms your team's expertise into reusable AI-powered tools.",
  },
];

const Features = () => {
  // Use the feature ID for the active tab state
  const [activeTabId, setActiveTabId] = useState<string>(featuresData[0].id); // Default to the first feature ID

  // Find the currently active feature based on the state
  const activeFeature = featuresData.find((f) => f.id === activeTabId) || featuresData[0];
  const ActiveIcon = activeFeature.icon; // Get the icon component for the active feature

  return (
    <section id="features" className={`content-section ${styles.featuresSection}`}> {/* Added class */}
      <div className="page-container">
        <h2 className="section-heading">
          Key <span>Features</span>
        </h2>

        {/* --- Tab Buttons --- */}
        <div className={styles.featureTabs}>
          {featuresData.map((feature) => (
            <button
              key={feature.id}
              className={activeTabId === feature.id ? styles.activeTab : ''}
              onClick={() => setActiveTabId(feature.id)}
              aria-selected={activeTabId === feature.id} // Use aria-selected for tabs
              role="tab" // ARIA role
            >
              {feature.tabLabel}
            </button>
          ))}
        </div>

        {/* --- Tab Content Area --- */}
        {/* Grid layout is now controlled in CSS for .featureDetail */}
        <div className={styles.featureContent} role="tabpanel"> {/* ARIA role */}
          {/* Always render based on activeFeature, animation handled by CSS */}
          <div className={styles.featureDetail} key={activeFeature.id}> {/* Add key for animation trigger */}

            {/* --- Left Column: Feature Text --- */}
            <div className={styles.featureText}>
              <h3>{activeFeature.title}</h3>
              <ul className={styles.featureList}>
                {activeFeature.points.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>
            </div>

            {/* --- Right Column: Feature Visual (Re-enabled) --- */}
            <div className={styles.featureVisual}>
              {/* Placeholder card structure using data */}
              <div className={styles.featureCard}>
                 <span className={styles.featureIconContainer}> {/* Container for icon */}
                   <ActiveIcon size={32} strokeWidth={1.5} /> {/* Use Lucide icon */}
                 </span>
                 <h3 className={styles.featureTitle}>{activeFeature.visualPlaceholderTitle}</h3>
                 <p className={styles.featureDescription}>{activeFeature.visualPlaceholderDescription}</p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;